import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWrench } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, LoadingModule, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject, snakeToTitleCase } from '@domain-group/javascript-utils'

import useRelatedEntities from '@hooks/useRelatedEntities'
import useTemplatePage from '@hooks/useTemplatePage'

import EntityActivity from '@components/EntityActivity'
import RelatedEntity from './components/RelatedEntity'

const MODAL_KEY = 'ViewTemplatePageDetailsModal'

const mappedPermissionKeys: { [key: string]: string } = {
  agencies: 'agencyIds',
  brand_themes: 'brandThemeIds',
  products: 'productIds',
  providers: 'providerIds',
  publications: 'publicationIds',
  template_groups: 'templateGroupIds',
}

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
  requestOptions: { [key: string]: boolean },
}

const ViewTemplatePageDetailsModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props

  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { templatePage } = modalPayload

  const templatePagePayload = useTemplatePage(templatePage, {
    performHttpRequests: !!templatePage?.id,
    requestOptions,
  })
  const { callbacks: { assignTemplatePagePermissions }, permissionCounts } = templatePagePayload

  const relatedEntitiesPayload = useRelatedEntities({
    filters: {
      subject_id: templatePage?.id,
      subject_type: 'TemplatePage',
    },
  })

  const { hasFilteredRelatedEntities, filteredRelatedEntities, loading } = relatedEntitiesPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Template" titleSecondLine="Details" />

      <SidebarModal.Content>
        <LoadingModule boxProps={{ marginTop: 'large' }} loading={loading} />

        {hasFilteredRelatedEntities && (
          <Form.Field label="Tasks" marginTop="large">
            {filteredRelatedEntities.map(relatedEntity => (
              <RelatedEntity key={relatedEntity.id} relatedEntity={relatedEntity} />
            ))}
          </Form.Field>
        )}

        <Form.Field label="Permissions" marginTop="large">
          <Box flexDirection="column" variant="white" padding="medium">
            {Object.keys(permissionCounts).map((permissionKey) => {
              const permissionCount = permissionCounts[permissionKey]

              return (
                <SidebarModal.ListItem
                  boxProps={{ padding: 'medium' }}
                  key={permissionKey}
                  title={snakeToTitleCase(permissionKey)}
                >
                  <SidebarModal.ListItem.Actions>
                    <Text
                      color="bodyFontLightColor"
                      marginRight="medium"
                    >
                      {permissionCount}
                    </Text>

                    <SidebarModal.ListItem.Icon
                      onClick={() => assignTemplatePagePermissions(mappedPermissionKeys[permissionKey])}
                      type="edit"
                    />
                  </SidebarModal.ListItem.Actions>
                </SidebarModal.ListItem>
              )
            })}
          </Box>

          <Button
            buttonStyle="secondaryUtility"
            marginTop="medium"
            icon={<FontAwesomeIcon icon={faWrench} />}
            onClick={() => assignTemplatePagePermissions('providerIds')}
            size="medium"
          >
            Manage Permissions
          </Button>
        </Form.Field>

        <Form.Field label="Activity" marginTop="large">
          <EntityActivity
            componentSettings={{
              conversation: {
                title: `Template Page #${templatePage?.id}`,
              },
            }}
            subjectType="TemplatePage"
            subjectId={templatePage?.id}
          />
        </Form.Field>

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button buttonStyle="secondaryUtility" onClick={() => closeModal()} size="large">
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ViewTemplatePageDetailsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
